import { useEffect, useMemo, useState } from "react";
import AdminGate from "../../v6common/AdminGate";
import * as api from "./api";
import { errorText, getMeta } from "../../inquiry/v6Api";
import { showToast } from "../../../utill/utill";
import { bizFmt, kstFull } from "../../inquiry/view";
import { EMPTY, initialFilter, toParams, isActive, seoulToday, nf } from "./logic";
import useChatData from "./useChatData";
import { Pager, SessionTable, QuestionList } from "./ChatList";
import { CategoryBars, KeywordPanel, FilterBar } from "./ChatPanels";
import ConvDrawer from "./ConvDrawer";
import "./chat.css";

// 대화 기록(시안 v6 #pg-chat, 계약 §13 · 대표 전용)
const MODES = [["date", "전체 대화"], ["cat", "분류별"], ["q", "질문만"], ["kw", "키워드"]];
const CHIPS = [["", "전체", "total"], ["answered", "응답", "answered"], ["failed", "실패", "failed"], ["escalated", "확인필요", "escalated"]];
const CHIP_CLS = { answered: "r-ok", failed: "r-fail", escalated: "r-esc" };

function download(blob, name) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function ChatBody({ go, params }) {
    const [filter, setFilter] = useState(() => initialFilter(params));
    const [qInput, setQInput] = useState(filter.q);
    const [mode, setMode] = useState(filter.cat != null ? "cat" : "date");
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [allTotal, setAllTotal] = useState(null);
    const [cats, setCats] = useState([]);
    const [meta, setMeta] = useState(null);
    const [logs, setLogs] = useState([]);
    const [conv, setConv] = useState(() => params?.sessionId ?? null); // 문의 관리 「대화 기록에서 보기」로 들어오면 바로 연다
    const { list, counts, err, loading, kw } = useChatData(filter, page, mode, refreshKey);

    const change = (patch) => { setFilter((f) => ({ ...f, ...patch })); setPage(1); };

    useEffect(() => {
        if (qInput.trim() === filter.q.trim()) return undefined;
        const t = setTimeout(() => change({ q: qInput }), 300);
        return () => clearTimeout(t);
    }, [qInput, filter.q]);

    const loadLogs = () => api.exportLogs().then((r) => setLogs(r || [])).catch(() => {});
    useEffect(() => {
        api.countSessions({}).then((c) => setAllTotal(c.total)).catch(() => {});
        loadLogs();
    }, [refreshKey]);
    useEffect(() => {
        api.quickCategories().then((r) => setCats(r || [])).catch(() => {});
        getMeta().then(setMeta).catch(() => {});
    }, []);

    const total = list?.total ?? 0;
    const catLabel = useMemo(() => {
        if (filter.cat == null) return "";
        return cats.find((c) => c.id === filter.cat)?.name
            || (counts?.by_category || []).find((r) => r.quick_category_id === filter.cat)?.name || `#${filter.cat}`;
    }, [filter.cat, cats, counts]);

    function pickMode(m) {
        setMode(m);
        if (m !== "cat" && filter.cat != null) change({ cat: null });
    }
    function pickKeyword(w) {
        const next = filter.q === w ? "" : w;
        setQInput(next);
        change({ q: next });
    }
    function reset() {
        setFilter({ ...EMPTY });
        setQInput("");
        setPage(1);
    }
    function refresh() {
        setRefreshKey((k) => k + 1);
        showToast("대화 기록을 새로고침했습니다.", "info");
    }

    async function exportAll() {
        if (!total) { showToast("내려받을 대화가 없습니다.", "info"); return; }
        try {
            const r = await api.exportCsv(toParams(filter));
            download(r.blob, r.name || `대화기록_${seoulToday()}.csv`);
            showToast(`대화 ${nf(total)}건을 엑셀로 내려받았습니다. 받은 기록이 남습니다.`, "success");
            loadLogs();
        } catch (e) { showToast(errorText(e), "error"); }
    }
    async function exportStore(store) {
        try {
            const r = await api.exportCsv({ business_number: store.business_number });
            download(r.blob, r.name || `대화기록_${store.name || "매장"}_${bizFmt(store.business_number)}_${seoulToday()}.csv`);
            showToast(`${store.name || "이 매장"} 대화를 엑셀로 내려받았습니다. 받은 기록이 남습니다.`, "success");
            loadLogs();
        } catch (e) { showToast(errorText(e), "error"); }
    }

    const res = filter.res;
    const n = (k) => (counts ? counts[k] : 0);
    const stats = { total, answered: res ? (res === "answered" ? total : 0) : n("answered"), failed: res ? (res === "failed" ? total : 0) : n("failed") };
    const items = list?.items || [];
    const active = isActive(filter);
    const last = logs[0];
    const period = `${filter.from || "처음"} ~ ${filter.to || seoulToday()}`;

    return (<>
        <div className="top">
            <div><h1>대화 기록 <span style={{ fontSize: 14, color: "var(--tx3)", fontWeight: 500 }}>{allTotal != null ? `(${nf(allTotal)}건)` : ""}</span></h1>
                <p>챗봇과 가맹점주가 나눈 대화를 확인하고, 상담 연결로 넘어간 건은 문의와 이어서 봅니다</p></div>
            <div className="top-act">
                <button className="btn" title="지금 걸러 본 대화를 메시지 단위로 내려받습니다" onClick={exportAll}><i className="fa-solid fa-file-excel" style={{ color: "#15803d" }}></i>엑셀</button>
                <button className="btn" onClick={refresh}><i className="fa-solid fa-rotate"></i>새로고침</button>
            </div>
        </div>
        <div className="panel" aria-busy={loading}>
            <div className="tabs chtabs">
                {MODES.map(([v, l]) => <button key={v} className={`tab ${mode === v ? "on" : ""}`} onClick={() => pickMode(v)}>{l}</button>)}
                <span className="sp"></span>
                <div className="rchips">
                    {CHIPS.map(([v, l, k]) => (
                        <button key={k} className={`rchip ${res === v ? "on" : ""} ${CHIP_CLS[v] || ""}`} onClick={() => change({ res: v })}>{l} <b>{nf(n(k))}</b></button>
                    ))}
                </div>
            </div>
            <div className="mfilter"><button className={`btn btn-sm ${open || active ? "btn-pri" : ""}`} onClick={() => setOpen((o) => !o)}><i className="fa-solid fa-filter"></i>필터{active ? " 사용 중" : ""}</button></div>
            <FilterBar f={filter} qInput={qInput} open={open} showReset={active || !!res} onChange={change} onQ={setQInput} onReset={reset} />
            {mode === "cat" && <CategoryBars rows={counts?.by_category || []} cat={filter.cat} catLabel={catLabel}
                onPick={(id) => change({ cat: filter.cat === id ? null : id })} />}
            {mode === "kw" && <KeywordPanel items={kw.items} error={kw.error} q={filter.q} period={period} stats={stats} onPick={pickKeyword} />}
            {err ? <div className="empty" role="alert">{err}</div>
                : list === null ? <div className="empty">불러오는 중…</div>
                    : mode === "q" ? <QuestionList items={items} onOpen={setConv} />
                        : <SessionTable items={items} onOpen={setConv} go={go} />}
            <div className="chfoot"><Pager page={page} total={total} onPage={setPage} /></div>
            {last && <div className="tbl-foot" style={{ background: "#f8fafc" }}><span><i className="fa-solid fa-file-excel" style={{ color: "#15803d" }}></i>
                {` 엑셀 다운로드 기록 ${logs.length}건 · 최근 ${kstFull(last.at)} ${last.by || ""} · ${last.file_name} (메시지 ${nf(last.row_count)}줄)`}</span></div>}
            <div className="tbl-foot"><span>{mode === "q" ? "질문을 누르면 그 대화가 열립니다" : "행을 누르면 대화 전체가 열립니다"}</span>
                <span className="mhide">「확인필요」인데 연결 문의가 없으면 접수가 빠진 건입니다</span></div>
        </div>
        {conv != null && <ConvDrawer id={conv} meta={meta} refreshKey={refreshKey} go={go} onClose={() => setConv(null)} onOpen={setConv}
            onChanged={() => setRefreshKey((k) => k + 1)} onStoreExport={exportStore} />}
    </>);
}

export default function ChatPage({ go, params }) {
    return <main className="inq-r"><AdminGate ownerOnly>{() => <ChatBody go={go} params={params} />}</AdminGate></main>;
}
